import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { Box } from '@mui/material';

import SearchIcon from '~/assets/icons/svgs/search.svg?react';
import Select from '~/components/ui/Select';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { DESKTOP_MIN_WIDTH, TABLET_MIN_WIDTH } from '~/constants/common';
import useQrCodeFolders from '~/hooks/useQrCodeFolders';
import FolderItem from '~/pages/MyQR/components/FolderItem';
import useSidebarStore from '~/store/useSidebarStore';
import { COLORS } from '~/styles/colors';

export default function MyFolders() {
  const { t } = useTranslation();

  const styles = useStyles();

  const [search, setSearch] = useState('');
  const [sorting, setSorting] = useState<'recent' | 'oldest'>('recent');

  const { data: folders } = useQrCodeFolders();

  const { isDesktopSidebarOpen } = useSidebarStore();

  const leftSidebarWidth = isDesktopSidebarOpen ? 323 : 160;

  const sortingTypes = [
    { label: t('folders:mostRecent'), value: 'recent' },
    { label: t('folders:oldest'), value: 'oldest' },
  ];

  const displayedFolders = useMemo(() => {
    if (!folders?.length) {
      return [];
    }

    const sortedFolders =
      sorting === 'recent'
        ? [...folders].sort((a, b) => new Date(b.createdAtRaw).getTime() - new Date(a.createdAtRaw).getTime())
        : [...folders].sort((a, b) => new Date(a.createdAtRaw).getTime() - new Date(b.createdAtRaw).getTime());

    if (search) {
      return sortedFolders.filter(({ name }) => name.toLowerCase().includes(search.toLowerCase()));
    }

    return sortedFolders;
  }, [folders, search, sorting]);

  return (
    <Box sx={styles.content}>
      <Box sx={styles.header}>
        <Text variant="h2.5">{t('folders:myFolders')}</Text>
        <Box sx={styles.headerRight}>
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('folders:searchFolder')}
            icon={<SearchIcon />}
            sx={{ minWidth: 240 }}
          />
          <Select
            items={sortingTypes}
            value={sorting}
            onChange={(event) => setSorting(event.target.value)}
            label={`${t('folders:sortBy')}:`}
            sx={{ minWidth: 250 }}
            itemBorderBottom={false}
            displayCheckIcon
            MenuProps={{
              slotProps: {
                paper: {
                  sx: {
                    borderRadius: '20px !important',
                  },
                },
              },
            }}
          />
        </Box>
      </Box>
      {displayedFolders.length ? (
        <Box sx={styles.foldersWrapper(leftSidebarWidth)}>
          {displayedFolders.map((folder) => {
            return <FolderItem key={folder.id} folder={folder} />;
          })}
        </Box>
      ) : (
        <Text variant="input15" sx={{ textAlign: 'center', color: COLORS.grey400, marginTop: '20px' }}>
          {t('folders:noResults')}
        </Text>
      )}
    </Box>
  );
}

function useStyles() {
  return {
    content: css`
      margin-bottom: 48px;
      max-width: 100%;
      overflow: hidden;
      @media (min-width: ${TABLET_MIN_WIDTH}px) {
        overflow: initial;
      }
    `,
    foldersWrapper: (leftSidebarWidth: number) => css`
      display: flex;
      flex-wrap: nowrap;
      gap: 12px;
      overflow-x: auto;
      margin-top: 20px;
      max-width: 100%;

      @media (min-width: ${TABLET_MIN_WIDTH}px) {
        max-width: calc(100dvw - 20px);
      }

      @media (min-width: ${DESKTOP_MIN_WIDTH}px) {
        max-width: calc(100dvw - ${leftSidebarWidth}px);
      }
    `,
    header: css`
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      @media (min-width: ${DESKTOP_MIN_WIDTH}px) {
        flex-direction: row;
        align-items: flex-end;
      }
    `,
    headerRight: css`
      display: flex;
      gap: 16px;
      align-items: flex-start;
      flex-direction: column;
      margin-right: 20px;
      margin-top: 20px;

      @media (min-width: ${TABLET_MIN_WIDTH}px) {
        margin-top: 0;
        flex-direction: row;
        align-items: flex-end;
      }
    `,
  };
}
